import {
    CARD_NETWORK,
    CLOUDINARY_TRANSFORMATION_WITHOUT_EMBOSS_MC,
    CLOUDINARY_TRANSFORMATION_WITHOUT_EMBOSS_VISA,
} from '../constants'
import {
    CARD_TYPE,
    CLOUDINARY_OVERLAY_TRANSFORMATION,
} from '../components/SmallBusiness/SmbConstants'

/**
 * @param string
 * @returns `true` if url is a Cloudinary URL, `false` otherwise
 */
export const isCloudinaryUrl = (string) => {
    if (!string) return false
    return string.includes('res.cloudinary.com/')
}

/**
 * Note: Cloudinary Public IDs include folder structure & exclude file extensions, query params
 * @param imageUrl
 * @returns `null` if imageUrl is falsey or if `imageUrl` is not a Cloudinary URL, otherwise the cloudinary publicId
 */
export const getCloudinaryPublicIdFromUrl = (imageUrl) => {
    if (!imageUrl || !isCloudinaryUrl(imageUrl)) return null

    // Split on root Cloudinary folder to strip any transformations that may exist
    const [, publicIdWithoutRoot] = imageUrl
        ?.replace(/.(jpe?g|png|webp)$/, '')
        ?.split('/GCG/')

    // Exclude query params
    const sanitizedPublicId = publicIdWithoutRoot?.split('?')[0]

    const publicId = `GCG/${sanitizedPublicId}`

    return publicId
}

/**
 * @param cardNetwork
 * @returns consumer cloudinary overlay transformation for card network excluding emboss lines
 */
export const getCloudinaryConsumerWithoutEmbossOverlay = (cardNetwork) => {
    if (!cardNetwork) return null

    if (cardNetwork?.toLowerCase() === CARD_NETWORK.VISA) {
        return CLOUDINARY_TRANSFORMATION_WITHOUT_EMBOSS_VISA
    } else if (cardNetwork?.toLowerCase() === CARD_NETWORK.MASTERCARD) {
        return CLOUDINARY_TRANSFORMATION_WITHOUT_EMBOSS_MC
    }
}

/**
 * @param cardNetwork
 * @param cardType
 * @param embossName
 * @param withYourLogoHere
 * @param merchant
 * @param isGiftProgram
 * @returns cloudinary overlay transformation for card network
 */
export const getCloudinarySmbEmbossOverlay = (
    cardNetwork,
    cardType,
    embossName = true,
    withYourLogoHere = false,
    merchant = null,
    isGiftProgram = false
) => {
    // If the network is not defined, or this is not a plastic card, then no overlay is needed.
    if (
        !cardNetwork ||
        ![CARD_TYPE.PHYSICAL, CARD_TYPE.PLASTIC].includes(cardType)
    ) {
        return null
    }

    // If merchant is defined, no overlay is needed.
    if (merchant) {
        return null
    }

    if (withYourLogoHere) {
        if (cardNetwork?.toLowerCase() === CARD_NETWORK.VISA) {
            return embossName
                ? CLOUDINARY_OVERLAY_TRANSFORMATION.VISA_WITH_YOUR_LOGO_HERE
                : CLOUDINARY_OVERLAY_TRANSFORMATION.VISA_WITHOUT_EMBOSS_WITH_YOUR_LOGO_HERE
        } else if (cardNetwork?.toLowerCase() === CARD_NETWORK.MASTERCARD) {
            return CLOUDINARY_OVERLAY_TRANSFORMATION.MASTERCARD_WITHOUT_EMBOSS_WITH_YOUR_LOGO_HERE
        }
    }

    if (cardNetwork?.toLowerCase() === CARD_NETWORK.VISA) {
        if (isGiftProgram) {
            return embossName
                ? CLOUDINARY_OVERLAY_TRANSFORMATION.VISA_GIFT
                : CLOUDINARY_OVERLAY_TRANSFORMATION.VISA_GIFT_WITHOUT_EMBOSS_NAME
        } else {
            return embossName
                ? CLOUDINARY_OVERLAY_TRANSFORMATION.VISA
                : CLOUDINARY_OVERLAY_TRANSFORMATION.VISA_WITHOUT_EMBOSS_NAME
        }
    } else if (cardNetwork?.toLowerCase() === CARD_NETWORK.MASTERCARD) {
        if (isGiftProgram) {
            return embossName
                ? CLOUDINARY_OVERLAY_TRANSFORMATION.MASTERCARD_GIFT
                : CLOUDINARY_OVERLAY_TRANSFORMATION.MASTERCARD_GIFT_WITHOUT_EMBOSS_NAME
        } else {
            return embossName
                ? CLOUDINARY_OVERLAY_TRANSFORMATION.MASTERCARD
                : CLOUDINARY_OVERLAY_TRANSFORMATION.MASTERCARD_WITHOUT_EMBOSS_NAME
        }
    }
}

/**
 * Plastic predesigns are driven by WcpProduct & WcpPackage combo.
 * Egift predesigns are driven by OnDemandGiftTemplates BUT we do have a virtual WcpProduct per card network
 * we need to account for on add to cart when passing open_loop_product_id.
 * @param wcpProductOrGiftTemplate - ideally fetched using useFetchOpenLoopProducts or useFetchGiftTemplates
 * @param dynamicEmboss - should be true when adding name on card/message on card/network overlay dynamically
 * @returns the publicId from a Cloudinary URL or the non-Cloudinary URL of a legacy image
 */
export const getCloudinaryPublicIdFromWcpProductOrGiftTemplate = (
    wcpProductOrGiftTemplate,
    dynamicEmboss = true
) => {
    if (!wcpProductOrGiftTemplate) return
    let publicId = null
    const isGiftTemplate =
        !wcpProductOrGiftTemplate.hasOwnProperty('virtual_card')
    const cloudinaryIdProperty = isGiftTemplate
        ? 'cloudinaryId'
        : 'cloudinary_id'

    if (
        dynamicEmboss &&
        wcpProductOrGiftTemplate?.raw_card_image_url &&
        isCloudinaryUrl(wcpProductOrGiftTemplate?.raw_card_image_url)
    ) {
        /**
         * For plastic predesigns with a raw_card_image_url, use that as the publicId since it ensures we're
         * using just the design & not a design with an overlay already on it stored in Cloudinary.
         * Ideally every plastic WcpProduct.WcpPackage would have a raw_card_image_url set.
         */
        publicId = getCloudinaryPublicIdFromUrl(
            wcpProductOrGiftTemplate?.raw_card_image_url
        )
    } else if (wcpProductOrGiftTemplate[cloudinaryIdProperty]) {
        /**
         * Virtual (gift templates) & plastic (open loop (wcp) products) designs have differently cased (camel vs. snake)
         * field names returned from the backend..
         */
        publicId = wcpProductOrGiftTemplate[cloudinaryIdProperty]
    } else {
        if (!isGiftTemplate) {
            publicId = wcpProductOrGiftTemplate?.card_image_emboss_preview_url // should include card emboss/network overlay in image
        } else {
            publicId =
                dynamicEmboss && wcpProductOrGiftTemplate?.cloudinaryId // Ideally every OnDemandGiftTemplate would have a cloudinary_id set
                    ? wcpProductOrGiftTemplate?.cloudinaryId
                    : wcpProductOrGiftTemplate?.imageUrl
        }
    }

    return publicId
}

export const determineCloudinaryIdForBaC = (activeCard) => {
    const cloudinaryId =
        activeCard.cloudinaryId ?? // for custom uploaded image
        activeCard?.design?.cloudinaryId ?? // for eGift predesign - from the gift template
        activeCard?.design?.cloudinary_id ?? // for plastic predesign - from the plastic open loop product
        activeCard?.design?.imageUrl ?? // fallback for eGift - from the gift template
        activeCard?.design?.card_image_emboss_preview_url // fallback for plastic - from the plastic open loop product

    return cloudinaryId
}
